import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useLicense } from "../../context/licenseContext";
import { useToast } from "../../context/toastContext";
import { useEscapeKey } from "../ui/useEscapeKey";
import apiClient from "../../api/apiClient";
import Loading from "../Loading/Loading";
import LabOrderSamples from "../../Pages/LabOrders/LabOrderSamples";
import OrderSuccessModal from "../OrderSuccessModal/OrderSuccessModal";
import "./OrderTestModal.css";

const createSampleFromMissing = (missingPanels) => {
    const tests = {
        cannabinoids: false,
        pesticides: false,
        heavy_metals: false,
        mycotoxins: false,
        yeast_and_mold: false,
        microbials: false,
        moisture_content: false,
        water_activity: false,
        foreign_matter: false,
        terpenes: false,
    };
    (missingPanels || []).forEach((panel) => {
        const key = panel === "moisture" ? "moisture_content" : panel;
        if (key in tests) tests[key] = true;
    });
    return {
        id: uuidv4(),
        name: "",
        batch_number: "",
        matrix: "F",
        tests,
    };
};

const OrderTestModal = ({ coa, missingPanels, onClose, fetchCoas }) => {
    const { currentLicense } = useLicense();
    const toast = useToast();
    useEscapeKey(onClose);

    const [availableLabs, setAvailableLabs] = useState([]);
    const [selectedLabId, setSelectedLabId] = useState("");
    const [labsLoading, setLabsLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [comments, setComments] = useState("");
    const [characterCount, setCharacterCount] = useState(500);
    const [samples, setSamples] = useState([createSampleFromMissing(missingPanels)]);
    const [successEmail, setSuccessEmail] = useState(null);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/lab-orders/available-labs?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => setAvailableLabs(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error("Error fetching labs:", err);
                setAvailableLabs([]);
            })
            .finally(() => setLabsLoading(false));
    }, [currentLicense]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (characterCount < 0) {
            setFormError("Comments cannot exceed 500 characters.");
            return;
        }

        const samplesPayload = samples.map((sample) => ({
            name: sample.name,
            batch_number: sample.batch_number,
            matrix: sample.matrix,
            tests: Object.entries(sample.tests)
                .filter(([, checked]) => checked)
                .map(([name]) => name),
        }));

        if (!samplesPayload.some((s) => s.tests.length > 0)) {
            setFormError("Please select at least one test.");
            return;
        }

        if (!selectedLabId) {
            setFormError("Please select a lab partner.");
            return;
        }

        setFormLoading(true);
        try {
            const res = await apiClient.post("/lab-orders/create-order", {
                productName: coa.product_name || "",
                seedToSaleNumber: "",
                comments,
                licenseNo: currentLicense.licenseNo,
                samples: samplesPayload,
                partnerId: parseInt(selectedLabId, 10),
                coaId: coa.id,
            });
            setSuccessEmail(res.data.email || "");
            if (fetchCoas) fetchCoas();
        } catch (err) {
            console.error("Error creating order:", err);
            setFormError(err.response?.data?.error || "Failed to create order.");
        } finally {
            setFormLoading(false);
        }
    };

    if (successEmail !== null) {
        return (
            <OrderSuccessModal
                email={successEmail}
                onClose={onClose}
            />
        );
    }

    const availableTests = (missingPanels || []).map((p) =>
        p === "moisture" ? "moisture_content" : p
    );

    return (
        <div className="otm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="otm" onClick={(e) => e.stopPropagation()}>
                <div className="otm__header">
                    <div>
                        <h3 className="otm__title">Order Tests</h3>
                        <p className="otm__subtitle">{coa.product_name}</p>
                    </div>
                    <button className="otm__close" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="otm__body">
                    {formLoading ? (
                        <Loading position="center" />
                    ) : (
                        <form className="otm__form" onSubmit={handleSubmit}>
                            <label className="otm__label" htmlFor="otm-lab">Lab Partner *</label>
                            {labsLoading ? (
                                <Loading position="inline" />
                            ) : availableLabs.length === 0 ? (
                                <p className="otm__error">No eligible lab partners available for your license type.</p>
                            ) : (
                                <select
                                    id="otm-lab"
                                    className="otm__input"
                                    required
                                    value={selectedLabId}
                                    onChange={(e) => setSelectedLabId(e.target.value)}
                                >
                                    <option value="">-- Select Lab Partner --</option>
                                    {availableLabs.map((lab) => (
                                        <option key={lab.id} value={lab.id}>
                                            {lab.name} ({lab.state || "N/A"})
                                        </option>
                                    ))}
                                </select>
                            )}

                            <hr className="otm__divider" />

                            <LabOrderSamples
                                samples={samples}
                                setSamples={setSamples}
                                createNewSample={() => createSampleFromMissing(missingPanels)}
                                availableTests={availableTests.length > 0 ? availableTests : null}
                            />

                            <div className="otm__comments">
                                <label className="otm__label" htmlFor="otm-comments">
                                    Additional Comments
                                </label>
                                <textarea
                                    id="otm-comments"
                                    className="otm__textarea"
                                    placeholder="Comments &/or Description"
                                    rows={3}
                                    maxLength={500}
                                    value={comments}
                                    onChange={(e) => {
                                        setComments(e.target.value);
                                        setCharacterCount(500 - e.target.value.length);
                                    }}
                                />
                                <p className={characterCount <= 0 ? "otm__char--over" : "otm__char"}>
                                    {characterCount} character{characterCount === 1 ? "" : "s"} remaining
                                </p>
                            </div>

                            {formError && <p className="otm__error">{formError}</p>}

                            <div className="otm__actions">
                                <button type="button" className="otm__btn otm__btn--cancel" onClick={onClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="otm__btn otm__btn--submit"
                                    disabled={labsLoading || availableLabs.length === 0 || !selectedLabId}
                                >
                                    Create Order
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderTestModal;
